import { AppContext } from "App";
import { Table, message } from "antd";
import { ROLES, STAGES } from "components/common/Constants";
import Loader from "components/common/Loader";
import { getFormalName, getNextStage } from "components/services";
import { getImageUploader } from "../../helper";
import { jobCard_page, jobCard_pageComplete } from "features/auth/userSlice";
import { roleSelector, UpdateJobCardPriority } from "features/jobinv/JobCardSlice";
import { allApi } from "features/jobinv/AllApi";
import {
	useGetJobcardQuery,
	useGetClientsQuery,
	useUpdateJobcardMutation,
	useFetchDispatchDetailsQuery,
	useFetchJobcardActivityLogQuery,
} from "features/store";
import { url as API_BASE_URL } from "components/common/api";
import React, { useEffect, useState, useContext, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./table.css";

// Extracted components and utilities
import DispatchDetailsModal from "./DispatchDetailsModal";
import { DispatchSummaryModal, DispatchPreviewModal } from "./DispatchInfoModals";
import ActivityLogModal from "./ActivityLogModal";
import {
	getLatestUpdate,
	formatDateTimeDisplay,
	formatDateDisplay,
	formatTimeDisplay,
	formatPhoneForWhatsApp,
	composeDispatchMessage,
	checkExpired,
	checkDeliveryToday,
} from "./cardsTableUtils";
import {
	getBaseColumns,
	getRateColumn,
	getStageColumn,
	getPriorityReadonlyColumn,
	getPaymentStatusColumn,
	getPriorityEditableColumn,
	getActionsColumn,
	getReviewColumn,
	getInfoColumn,
	getCompletedInfoColumn,
	getDispatchCheckboxColumn,
	getSelectColumn,
} from "./CardsTableColumns";
import {
	ConfirmForwardModal,
	DigitizerValidationModal,
	CourierModeValidationModal,
	InvoiceValidationModal,
	JobcardInfoModal,
} from "./ValidationModals";
import { getForwardColumn } from "./ForwardButton";
import {
	useModalStates,
	useForwardValidation,
	useConfirmForward,
	useDispatchSelection,
	useDataMaps,
} from "./useCardsTableHandlers";

function CardsTable({
	cards,
	isCompleted,
	tableKey,
	clients,
	count,
	page,
	client_company,
	fetchJob,
	user,
	selectedRowKeys,
	setSelectedRowKeys,
	recordBig,
	setRecordBig,
	onDispatchButtonStateChange,
	pageSize = 10,
	onPageSizeChange,
	pageSizeOptions = ["10", "20", "50", "100"],
}) {
	const role = useSelector(roleSelector);
	const { jobcard_page, jobcard_page_complete } = useSelector((state) => state.user);
	const { currentTablePage, setCurrentTablePage } = useContext(AppContext);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Priority editing state
	const [editingPriorityId, setEditingPriorityId] = useState(null);
	const [editingPriorityValue, setEditingPriorityValue] = useState(null);
	const [tableColumns, setTableColumns] = useState([]);

	// Dispatch modal state
	const [dispatchPreview, setDispatchPreview] = useState(null);
	const [isDispatchModalVisible, setIsDispatchModalVisible] = useState(false);
	const [isSavingDispatchDetails, setIsSavingDispatchDetails] = useState(false);

	// API hooks
	const [updateJobcard, updateResponseInfo] = useUpdateJobcardMutation();
	const { isLoading: isUpdating } = updateResponseInfo;

	// Custom hooks for modal states
	const {
		digitizerValidationModal,
		setDigitizerValidationModal,
		closeDigitizerModal,
		courierModeValidationModal,
		setCourierModeValidationModal,
		closeCourierModal,
		invoiceValidationModal,
		setInvoiceValidationModal,
		closeInvoiceModal,
		confirmModal,
		setConfirmModal,
		closeConfirmModal,
		infoModal,
		setInfoModal,
		closeInfoModal,
		completedInfoModal,
		setCompletedInfoModal,
		closeCompletedInfoModal,
		activityLogModal,
		setActivityLogModal,
		closeActivityLogModal,
	} = useModalStates();

	// Custom hooks for handlers
	const { handleDispatchCheckboxChange } = useDispatchSelection({
		setSelectedRowKeys,
		setRecordBig,
	});

	const { validateAndForward } = useForwardValidation({
		user,
		setDigitizerValidationModal,
		setCourierModeValidationModal,
		setInvoiceValidationModal,
		setConfirmModal,
		navigate,
	});

	const { handleConfirmForward } = useConfirmForward({
		confirmModal,
		setConfirmModal,
		setDigitizerValidationModal,
		setCourierModeValidationModal,
		setInvoiceValidationModal,
		updateJobcard,
		dispatch,
		allApi,
	});

	// Data fetching
	const { data: activityLogData, isLoading: isActivityLogLoading } =
		useFetchJobcardActivityLogQuery(activityLogModal.jobcardId || 0, {
			skip: !activityLogModal.visible || !activityLogModal.jobcardId,
		});

	const { data: jobcardDetail } = useGetJobcardQuery(infoModal.jobcardId, {
		skip: !infoModal.jobcardId,
	});

	const { data: dispatchDetailsData = [], isFetching: isDispatchDetailsLoading } =
		useFetchDispatchDetailsQuery(completedInfoModal.jobcardId, {
			skip: !completedInfoModal.jobcardId,
		});

	const { data: clientsList } = useGetClientsQuery();

	const { clientMap, jobcardMap } = useDataMaps({ clientsList, cards });

	// Helper function to check if a jobcard is rejected
	const isJobcardRejected = (card) => {
		const isNullStage = !card.current_stage || card.current_stage === null || card.current_stage === "";
		const isDigitizerStage = card.is_degitizer_stage === true || card.is_degitizer_stage === "true";
		return isNullStage && isDigitizerStage;
	};

	// Transform data source
	const dataSource = cards?.map((card) => {
		const isRejected = isJobcardRejected(card);
		return {
			...card,
			key: card.id,
			client: client_company?.filter((item) => item.id === card.client)[0]?.company_name,
			current_stage: card.current_stage == null
				? isRejected ? null : STAGES.CREATION
				: card.current_stage,
			last_update: getLatestUpdate(card),
			is_degitizer_stage: card.is_degitizer_stage,
			_originalCurrentStage: card.current_stage,
			_isRejected: isRejected,
		};
	});

	// Computed values
	const safeSelectedRowKeys = Array.isArray(selectedRowKeys) ? selectedRowKeys : [];
	const resolvedPageSize = Number(pageSize) || 10;
	const resolvedPageSizeOptions = (pageSizeOptions?.length ? pageSizeOptions : ["10", "20", "50", "100"]).map(
		(option) => option.toString()
	);
	const totalRecords = typeof count === "number" ? count : dataSource?.length || 0;
	const currentPage =
		page === "jobcard_page_complete"
			? jobcard_page_complete
			: page === "jobcard_page"
				? jobcard_page
				: currentTablePage || 1;

	const storedUser = React.useMemo(() => {
		try {
			return JSON.parse(localStorage.getItem("user")) || {};
		} catch {
			return {};
		}
	}, []);

	const activeRole = user?.roll || storedUser?.roll || role;
	const isViewer = activeRole === ROLES.VIEWER;
	const showFillDetailsButton = user?.roll === ROLES.DISPATCH;
	const dispatchDetails = Array.isArray(dispatchDetailsData) ? dispatchDetailsData : [];
	const allCompleted = cards?.every((record) => record.current_stage === "completed");

	// Handle fill details click
	const handleFillDetailsClick = useCallback(() => {
		if (!safeSelectedRowKeys.length) {
			message.info("Select a jobcard to fill dispatch details.");
			return;
		}
		setIsDispatchModalVisible(true);
	}, [safeSelectedRowKeys.length]);

	// Handle table pagination
	const handleTablePaginationChange = (nextPage, nextPageSize) => {
		const normalizedPageSize = nextPageSize || resolvedPageSize;
		const isCompletedPage = page === "jobcard_page_complete";
		const isMainPage = page === "jobcard_page";
		const pageChangeAction = isCompletedPage ? jobCard_pageComplete : isMainPage ? jobCard_page : null;

		if (normalizedPageSize !== resolvedPageSize) {
			if (typeof onPageSizeChange === "function") {
				onPageSizeChange(normalizedPageSize);
			}
			if (pageChangeAction) {
				dispatch(pageChangeAction(1));
			} else if (typeof setCurrentTablePage === "function") {
				setCurrentTablePage(1);
			}
			return;
		}

		if (pageChangeAction) {
			dispatch(pageChangeAction(nextPage));
		} else if (typeof setCurrentTablePage === "function") {
			setCurrentTablePage(nextPage);
		}
	};

	// WhatsApp share trigger
	const triggerWhatsAppShare = useCallback(
		(formValues, jobcardIds) => {
			if (!Array.isArray(jobcardIds) || !jobcardIds.length || !formValues) return;
			if (typeof window === "undefined") return;

			const groupedByClient = jobcardIds.reduce((acc, id) => {
				const jobcard = jobcardMap.get(id);
				if (!jobcard) return acc;
				const client = clientMap.get(jobcard.client);
				if (!client) return acc;
				if (!acc[client.id]) {
					acc[client.id] = { client, jobcards: [] };
				}
				acc[client.id].jobcards.push(jobcard);
				return acc;
			}, {});

			const deliveryDateText =
				formValues?.delivery_date?.format?.("DD MMM YYYY") || formatDateDisplay(formValues?.delivery_date);
			const deliveryTimeText =
				formValues?.delivery_time?.format?.("hh:mm A") || formatTimeDisplay(formValues?.delivery_time);
			const deliveryNoteText = formValues?.delivery_note?.trim() || "—";

			Object.values(groupedByClient).forEach(({ client, jobcards }) => {
				const phone =
					formatPhoneForWhatsApp(client?.phone) || formatPhoneForWhatsApp(client?.alternate_phone_number);

				if (!phone) {
					message.warning(
						`WhatsApp number not found for ${client?.company_name || client?.person_name || "client"}.`
					);
					return;
				}

				const whatsappMessage = composeDispatchMessage({
					clientName: client?.person_name || client?.company_name,
					companyName: client?.company_name,
					jobcards,
					deliveryDate: deliveryDateText,
					deliveryTime: deliveryTimeText,
					deliveryNote: deliveryNoteText,
				});

				const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(whatsappMessage)}`;
				window.open(whatsappUrl, "_blank", "noopener,noreferrer");
			});
		},
		[clientMap, jobcardMap]
	);

	// Dispatch modal submit handler
	const handleDispatchModalSubmit = async (formData, formValues) => {
		const jobcardIdsForShare = [...safeSelectedRowKeys];
		try {
			setIsSavingDispatchDetails(true);
			const response = await fetch(`${API_BASE_URL}/api/dispatch-details/`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${localStorage.getItem("userToken")}`,
				},
				body: formData,
			});

			if (!response.ok) {
				let errorMessage = "Failed to save dispatch details";
				try {
					const errorData = await response.json();
					if (errorData?.detail) {
						errorMessage = errorData.detail;
					}
				} catch (err) {
					// ignore JSON parse errors
				}
				throw new Error(errorMessage);
			}

			message.success("Dispatch details saved successfully.");
			if (formValues && jobcardIdsForShare.length) {
				triggerWhatsAppShare(formValues, jobcardIdsForShare);
			}
			setIsDispatchModalVisible(false);
			if (typeof setSelectedRowKeys === "function") {
				setSelectedRowKeys([]);
			}
			if (typeof setRecordBig === "function") {
				setRecordBig([]);
			}
			dispatch(
				allApi.util.invalidateTags([
					"Card",
					"CardCompleted",
					"Topcard",
					"CLIENT_COMPANY",
					"CLIENT_COMPANY_COMPLETED",
					"Delivery",
					"DispatchDetails",
				])
			);
		} catch (error) {
			message.error(error?.message || "Failed to save dispatch details");
		} finally {
			setIsSavingDispatchDetails(false);
		}
	};

	// Dispatch button state effect
	useEffect(() => {
		if (typeof onDispatchButtonStateChange !== "function") return;

		onDispatchButtonStateChange((prev) => {
			const nextState = {
				visible: showFillDetailsButton,
				disabled: !safeSelectedRowKeys.length,
				onClick: handleFillDetailsClick,
			};

			if (
				prev &&
				prev.visible === nextState.visible &&
				prev.disabled === nextState.disabled &&
				prev.onClick === nextState.onClick
			) {
				return prev;
			}

			return nextState;
		});
	}, [onDispatchButtonStateChange, showFillDetailsButton, safeSelectedRowKeys.length, handleFillDetailsClick]);

	// Cleanup effect
	useEffect(() => {
		return () => {
			if (typeof onDispatchButtonStateChange !== "function") return;
			onDispatchButtonStateChange((prev) => {
				const resetState = { visible: false, disabled: true, onClick: null };
				if (
					prev &&
					prev.visible === resetState.visible &&
					prev.disabled === resetState.disabled &&
					prev.onClick === resetState.onClick
				) {
					return prev;
				}
				return resetState;
			});
		};
	}, [onDispatchButtonStateChange]);

	// Build columns
	const columns = [
		// Dispatch checkbox column
		...(activeRole === ROLES.DISPATCH && !isCompleted && typeof setSelectedRowKeys === "function"
			? [getDispatchCheckboxColumn({ safeSelectedRowKeys, handleDispatchCheckboxChange })]
			: []),

		// Select column for CUTTING and ADMIN
		...((activeRole === ROLES.CUTTING || activeRole === ROLES.ADMIN) &&
			!isCompleted &&
			typeof setSelectedRowKeys === "function" &&
			typeof setRecordBig === "function"
			? [getSelectColumn({ safeSelectedRowKeys, recordBig, setSelectedRowKeys, setRecordBig })]
			: []),

		// Base columns
		...getBaseColumns({ isCompleted, navigate, user }),

		// Rate column for viewer
		...(isViewer ? [getRateColumn()] : []),

		// Stage column
		getStageColumn({ isCompleted }),

		// Priority readonly column (non-admin/design)
		...(![ROLES.ADMIN, ROLES.DESIGN].includes(user.roll) ? [getPriorityReadonlyColumn()] : []),

		// Payment status column
		getPaymentStatusColumn({ user, updateJobcard }),

		// Admin/Design specific columns
		...([ROLES.ADMIN, ROLES.DESIGN].includes(user.roll)
			? [
				// Priority editable column (if not all completed)
				...(!allCompleted
					? [
						getPriorityEditableColumn({
							editingPriorityId,
							editingPriorityValue,
							setEditingPriorityId,
							setEditingPriorityValue,
							dispatch,
							UpdateJobCardPriority,
							user,
						}),
					]
					: []),
				// Actions column
				...(user.roll === ROLES.ADMIN || user.roll === ROLES.DESIGN
					? [getActionsColumn({ navigate, setActivityLogModal })]
					: []),
			]
			: []),

		// Forward column
		...([
			ROLES.ADMIN,
			ROLES.DESIGN,
			ROLES.PACKING,
			ROLES.PRODUCTION,
			ROLES.ACCOUNT,
			ROLES.PAYMENT,
			ROLES.DISPATCH,
			ROLES.CUTTING,
			ROLES.SECONDARY_PROCESS,
			ROLES.EMBROIDARY,
		].includes(user.roll)
			? [getForwardColumn({ user, onForward: validateAndForward })]
			: []),

		// Review column (admin only)
		...(user.roll === ROLES.ADMIN ? [getReviewColumn({ updateJobcard })] : []),

		// Info column for packing/billing/dispatch (in-progress)
		...(!isCompleted && [ROLES.PACKING, ROLES.ACCOUNT, ROLES.DISPATCH].includes(user.roll)
			? [getInfoColumn({ setInfoModal })]
			: []),

		// Completed info column
		...(isCompleted ? [getCompletedInfoColumn({ setCompletedInfoModal })] : []),
	];

	// Filter and reorder columns based on role
	useEffect(() => {
		let filteredColumns = columns;

		if (
			[
				ROLES.CUTTING,
				ROLES.PRODUCTION,
				ROLES.SECONDARY_PROCESS,
				ROLES.EMBROIDARY,
				ROLES.ACCOUNT,
				ROLES.PAYMENT,
			].includes(role) &&
			role !== ROLES.ADMIN &&
			role !== ROLES.DESIGN
		) {
			if (role === ROLES.CUTTING) {
				filteredColumns = columns.filter((column) => column.key !== "update");
			} else {
				filteredColumns = columns.filter(
					(column) => column.key !== "update" && column.title !== "Generate Qty"
				);
			}
		} else if (page === "jobcard_page_complete") {
			filteredColumns = columns.filter((column) => column.title !== "Generate Qty");
		}

		// Move Actions column to the end
		const idx = filteredColumns.findIndex((c) => c.key === "update");
		if (idx > -1) {
			const [updateCol] = filteredColumns.splice(idx, 1);
			filteredColumns.push(updateCol);
		}

		setTableColumns(filteredColumns);
	}, [role, page, columns]);

	if (!cards) return <Loader />;

	return (
		<>
			{isUpdating && <Loader />}
			<Table
				loading={fetchJob}
				style={{ width: "100%" }}
				dataSource={dataSource}
				columns={tableColumns}
				rowClassName={(record) => {
					const selectedClass = `selected-table-${safeSelectedRowKeys.includes(record?.id)}`;
					if (!isCompleted && record?.priority) {
						return `priority-${record?.priority} ${selectedClass}`;
					} else if (!(record.current_stage === STAGES.COMPLETED) && checkExpired(record)) {
						return `expired ${selectedClass}`;
					} else if (!(record.current_stage === STAGES.COMPLETED) && checkDeliveryToday(record)) {
						return `ongoing ${selectedClass}`;
					}
					return selectedClass;
				}}
				pagination={{
					total: totalRecords,
					pageSize: resolvedPageSize,
					current: currentPage,
					showSizeChanger: true,
					pageSizeOptions: resolvedPageSizeOptions,
					onChange: handleTablePaginationChange,
				}}
			/>

			{/* Confirm Forward Modal */}
			<ConfirmForwardModal
				visible={confirmModal.visible}
				record={confirmModal.record}
				nextStage={confirmModal.nextStage}
				onConfirm={handleConfirmForward}
				onCancel={closeConfirmModal}
			/>

			{/* Dispatch Details Modal */}
			<DispatchDetailsModal
				open={isDispatchModalVisible}
				onCancel={() => setIsDispatchModalVisible(false)}
				onSubmit={handleDispatchModalSubmit}
				loading={isSavingDispatchDetails}
				jobcardIds={safeSelectedRowKeys}
			/>

			{/* Dispatch Summary Modal */}
			<DispatchSummaryModal
				visible={completedInfoModal.visible}
				jobcardId={completedInfoModal.jobcardId}
				jobcardSerialNumber={
					dataSource?.find((j) => j.id === completedInfoModal.jobcardId)?.serial_number ||
					completedInfoModal.jobcardId
				}
				loading={isDispatchDetailsLoading}
				details={dispatchDetails}
				onClose={closeCompletedInfoModal}
				onPreview={(payload) => setDispatchPreview(payload)}
				formatters={{ formatDateTimeDisplay, formatDateDisplay, formatTimeDisplay }}
			/>

			{/* Validation Modals */}
			<DigitizerValidationModal
				visible={digitizerValidationModal.visible}
				incompleteVersions={digitizerValidationModal.incompleteVersions}
				onClose={closeDigitizerModal}
			/>

			<CourierModeValidationModal
				visible={courierModeValidationModal.visible}
				incompleteVersions={courierModeValidationModal.incompleteVersions}
				onClose={closeCourierModal}
			/>

			<InvoiceValidationModal
				visible={invoiceValidationModal.visible}
				incompleteVersions={invoiceValidationModal.incompleteVersions}
				onClose={closeInvoiceModal}
			/>

			{/* Dispatch Preview Modal */}
			<DispatchPreviewModal preview={dispatchPreview} onClose={() => setDispatchPreview(null)} />

			{/* Activity Log Modal */}
			<ActivityLogModal
				open={activityLogModal.visible}
				onCancel={closeActivityLogModal}
				jobcardId={activityLogModal.jobcardId}
				jobcardSerialNumber={
					dataSource?.find((j) => j.id === activityLogModal.jobcardId)?.serial_number ||
					activityLogModal.jobcardId
				}
				activityData={activityLogData}
				loading={isActivityLogLoading}
			/>

			{/* Jobcard Info Modal */}
			<JobcardInfoModal
				visible={infoModal.visible}
				jobcardId={infoModal.jobcardId}
				jobcardDetail={jobcardDetail}
				clientsList={clientsList}
				onClose={closeInfoModal}
				onOpenFullPage={() => {
					const id = infoModal.jobcardId;
					closeInfoModal();
					navigate(`/detail/${id}`);
				}}
			/>
		</>
	);
}

export default CardsTable;
